import React, { useState, useEffect } from 'react';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import '@progress/kendo-theme-default/dist/all.css';

export interface SelectableItem {
    id: string;
    name: string;
    code?: string;
    description?: string;
    [key: string]: any;
}

interface MultiSelectListProps {
    title: string;
    selectedItems: SelectableItem[];
    availableItems: SelectableItem[];
    onSelectionChange: (selectedItems: SelectableItem[]) => void;
    loading?: boolean;
    textField?: string;
    valueField?: string;
    placeholder?: string;
}

const MultiSelectList: React.FC<MultiSelectListProps> = ({
    title,
    selectedItems,
    availableItems,
    onSelectionChange,
    loading = false,
    textField = 'name',
    valueField = 'id',
    placeholder = 'Seleziona elementi...'
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [localSelection, setLocalSelection] = useState<SelectableItem[]>(selectedItems);

    useEffect(() => {
        setLocalSelection(selectedItems);
    }, [selectedItems]);

    const handleOpenDialog = () => {
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setLocalSelection(selectedItems); // Reset to original selection
    };

    const handleSaveSelection = () => {
        onSelectionChange(localSelection);
        setIsDialogOpen(false);
    };

    const handleMultiSelectChange = (event: any) => {
        setLocalSelection(event.value);
    };

    const handleRemoveItem = (itemId: string) => {
        const newSelection = localSelection.filter(item => item[valueField] !== itemId);
        setLocalSelection(newSelection);
    };

    return (
        <div className="multi-select-list">
            <div className="form-group">
                <label>{title} ({selectedItems.length} selezionati)</label>
                <div className="selected-items-summary">
                    {selectedItems.length === 0 ? (
                        <span className="no-selection">Nessun elemento selezionato</span>
                    ) : (
                        <div className="selected-items-preview">
                            {selectedItems.slice(0, 3).map(item => (
                                <span key={item[valueField]} className="selected-item-chip">
                                    {item[textField]}
                                </span>
                            ))}
                            {selectedItems.length > 3 && (
                                <span className="more-items">+{selectedItems.length - 3} altri</span>
                            )}
                        </div>
                    )}
                </div>
                <Button onClick={handleOpenDialog} disabled={loading}>
                    Gestisci {title}
                </Button>
            </div>

            {isDialogOpen && (
                <Dialog
                    title={`Gestisci ${title}`}
                    onClose={handleCloseDialog}
                    width={800}
                    height={600}
                >
                    <div style={{ padding: '20px' }}>
                        <div className="form-group">
                            <label>Seleziona {title.toLowerCase()}:</label>
                            <MultiSelect
                                data={availableItems}
                                value={localSelection}
                                onChange={handleMultiSelectChange}
                                textField={textField}
                                dataItemKey={valueField}
                                placeholder={placeholder}
                                style={{ width: '100%' }}
                                loading={loading}
                            />
                        </div>

                        {localSelection.length > 0 && (
                            <div className="form-group">
                                <label>Elementi selezionati ({localSelection.length}):</label>
                                <Grid
                                    data={localSelection}
                                    style={{ height: '300px' }}
                                >
                                    <GridColumn field={textField} title="Nome" width="250px" />
                                    {localSelection.some(item => item.code) && (
                                        <GridColumn field="code" title="Codice" width="150px" />
                                    )}
                                    {localSelection.some(item => item.description) && (
                                        <GridColumn field="description" title="Descrizione" />
                                    )}
                                    <GridColumn
                                        title="Azioni"
                                        width="100px"
                                        cell={({ dataItem }) => (
                                            <Button
                                                onClick={() => handleRemoveItem(dataItem[valueField])}
                                                icon="delete"
                                                fillMode="flat"
                                                size="small"
                                            >
                                                Rimuovi
                                            </Button>
                                        )}
                                    />
                                </Grid>
                            </div>
                        )}
                    </div>

                    <DialogActionsBar>
                        <Button onClick={handleCloseDialog}>
                            Annulla
                        </Button>
                        <Button onClick={handleSaveSelection} themeColor="primary">
                            Salva Modifiche ({localSelection.length})
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            <style jsx>{`
                .multi-select-list {
                    margin: 10px 0;
                }

                .selected-items-summary {
                    margin: 8px 0;
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    background-color: #f9f9f9;
                    min-height: 40px;
                }

                .no-selection {
                    color: #999;
                    font-style: italic;
                }

                .selected-items-preview {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 5px;
                }

                .selected-item-chip {
                    background-color: #e3f2fd;
                    color: #1976d2;
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .more-items {
                    color: #666;
                    font-size: 12px;
                    padding: 4px 8px;
                }

                .form-group {
                    margin-bottom: 15px;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 5px;
                    font-weight: bold;
                    color: #333;
                }
            `}</style>
        </div>
    );
};

export default MultiSelectList;